/**
 * Display helpers shared by the 系统健康透视 page shell, its KPI strip, its
 * ladder table and its inspector.
 *
 * Money formatting is NOT here — it comes from `components/formatters.ts`
 * (`formatCnyCents`), which is the console's single money surface. What
 * lives here is the vocabulary this page renders grades and rates in, kept
 * in one module so the strip and the inspector cannot start disagreeing
 * about what `unknown` looks like.
 */

import type { I18nKey } from '../../i18n/index.ts';
import type { ChipTone, KpiTone, LampTone } from '../../ui/index.ts';
import type { TrafficSeverity } from '../../components/trafficHealth.ts';
import type { HealthGrade, MapNode, ObservedTraffic } from './types.ts';

const INTEGER_FORMATTER = new Intl.NumberFormat('en-US');

/**
 * The text to render for a node.
 *
 * `model.ts` leaves `label` empty for the null-id aggregate buckets — the
 * SKU legacy bucket (`skuId === null`, usage charged before SKU attribution
 * existed) and the unattributed supply buckets (`channelSlug === null`). It
 * is right that it does: the data layer has no business holding display
 * copy, and those buckets have no identity to name themselves with.
 *
 * But an empty string in a table cell is not "no name", it is a blank row
 * the operator cannot act on with any idea of what it is — and the buckets
 * are load-bearing, because they are exactly what makes the table's totals
 * reconcile with the fleet totals. So the naming happens here, once, where
 * the dictionary lives.
 *
 * Every other node already has a real label and falls straight through.
 */
export function nodeDisplayLabel(node: MapNode, t: (key: I18nKey) => string): string {
  if (node.label.length > 0) return node.label;
  if (node.kind === 'sku') return t('health_perspective.node.legacy_bucket');
  if (node.kind === 'channel' || node.kind === 'upstream_model') {
    return t('health_perspective.node.unattributed_upstream');
  }
  return node.label;
}

export function formatInteger(value: number): string {
  return INTEGER_FORMATTER.format(value);
}

/**
 * A count that may be unknown. Renders an em dash, never a zero.
 *
 * The whole reason the refusal counts are nullable is that a substituted zero
 * on a gateway that cannot report them turns "we cannot tell" into "none were
 * refused" — which then renders every failure as ours and states it as fact.
 */
export function formatMaybeInteger(value: number | null): string {
  return value === null ? '—' : INTEGER_FORMATTER.format(value);
}

/**
 * Whether `attributableErrors / attributableRequests` may be printed as the
 * fraction behind a downstream rate.
 *
 * Two different unknowns sit behind a `false`, and both have to suppress it:
 *
 *   - no refusal counter at all, so there is no rate and no fraction;
 *   - the counter but no code split, so there IS a fraction — computed by
 *     deducting nothing — and printing it would leave it to look precise
 *     when the deduction is exactly the part we could not do.
 *
 * Shared because the ladder table and the inspector render the same numbers
 * about the same node side by side, and a caveat applied on one surface only
 * is an operator reading a hedge in the table and a bare figure in the panel
 * they opened from it.
 */
export function downstreamDetailIsPrintable(traffic: ObservedTraffic): boolean {
  if (traffic.downstreamErrorRate === null || traffic.attributableRequests === null) return false;
  return !(traffic.expectedRefusals === null && (traffic.refusedBeforeUpstream ?? 0) > 0);
}

/** A 0..1 fraction as a percent. Null / non-finite renders as an em dash. */
export function formatRate(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(1)}%`;
}

export function formatDurationMs(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—';
  if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(2)}s`;
  return `${Math.round(value)}ms`;
}

/** Whole-percent share, already expressed in percent units (0..100). */
export function formatPercentPoints(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return `${value.toFixed(value >= 10 ? 0 : 1)}%`;
}

export const GRADE_LABEL_KEY: Readonly<Record<HealthGrade, I18nKey>> = {
  ok: 'health_perspective.grade.ok',
  warn: 'health_perspective.grade.warn',
  bad: 'health_perspective.grade.bad',
  idle: 'health_perspective.grade.idle',
  unknown: 'health_perspective.grade.unknown',
};

/**
 * Grade → chip tone.
 *
 * `idle` and `unknown` deliberately do NOT share a tone. `idle` is "there
 * was nothing to judge" and reads neutral; `unknown` is "the slice that
 * would have judged it failed to load", which is a degraded read and warns.
 * Collapsing the two — the failure `types.ts` calls out — would turn a
 * broken read into a reassuring grey dot.
 */
export const GRADE_CHIP_TONE: Readonly<Record<HealthGrade, ChipTone>> = {
  ok: 'ok',
  warn: 'warn',
  bad: 'bad',
  idle: 'neutral',
  unknown: 'warn',
};

export const GRADE_LAMP_TONE: Readonly<Record<HealthGrade, LampTone>> = {
  ok: 'ok',
  warn: 'warn',
  bad: 'bad',
  idle: 'off',
  unknown: 'warn',
};

export function severityKpiTone(severity: TrafficSeverity): KpiTone {
  if (severity === 'critical') return 'bad';
  if (severity === 'warning') return 'warn';
  return 'default';
}

export function severityLamp(severity: TrafficSeverity): LampTone | undefined {
  if (severity === 'critical') return 'bad';
  if (severity === 'warning') return 'warn';
  return undefined;
}

export function severityTextClass(severity: TrafficSeverity): string {
  if (severity === 'critical') return 'text-bad';
  if (severity === 'warning') return 'text-warn';
  return 'text-paper';
}

/**
 * How a ladder rung's endpoint is named, everywhere it is named.
 *
 * `dragoncode · anthropic_oauth / claude-fable-5` — supplier first, because
 * that is what an operator reasons about and decides with; the channel stays
 * because it identifies WHICH lane of that supplier, and several channels
 * share one. Four production channels (`anthropic_kiro`, `anthropic_oauth`,
 * `anthropic_oauth2api`, `openai_oauth`) are all `dragoncode`, and slugs
 * alone hide that `claude-code/claude-fable-5` falls back from our own pool
 * onto a different SUPPLIER.
 *
 * With no name — the upstreams read failed, or the id is not in it because
 * the upstream was deleted after the routing row was written — the channel
 * slug leads instead. Never blank, never a raw id, and never a guess.
 */
export interface EndpointParts {
  readonly upstreamName: string | null;
  readonly channelSlug: string | null;
  readonly upstreamModelName?: string | null;
}

export function endpointLead(parts: EndpointParts): string | null {
  return parts.upstreamName ?? parts.channelSlug ?? null;
}

/** True when the channel is worth printing after the lead — i.e. it is not
 *  already the lead. */
export function endpointHasChannel(parts: EndpointParts): boolean {
  return parts.channelSlug !== null && parts.upstreamName !== null;
}

/** Flat text, for contexts that cannot style parts (aria labels, one-liners). */
export function formatEndpoint(parts: EndpointParts, unknown: string): string {
  const lead = endpointLead(parts) ?? unknown;
  const channel = endpointHasChannel(parts) ? ` · ${parts.channelSlug ?? ""}` : "";
  const model =
    parts.upstreamModelName === undefined ? "" : ` / ${parts.upstreamModelName ?? unknown}`;
  return `${lead}${channel}${model}`;
}
